import {useLogger, useTranslation} from '#imports';
import {readonlyState} from '@malt/utils';
import {pushVJoySnackbar} from '@maltjoy/core-vue';
import {ref} from 'vue';
import {FetchError} from 'ofetch';

export type Thumbnails = {
    highResolutionUrl: string;
    mediumResolutionUrl?: string;
    lowResolutionUrl?: string;
};

type VideoInfos = {
    title: string;
    thumbnails: Thumbnails;
};

class VideoNotFoundError extends Error {
    constructor(url: string) {
        super(`No video found for ${url}`);
        Object.setPrototypeOf(this, new.target.prototype);
    }
}

export function useVideoInfos() {
    const logger = useLogger();
    const {t} = useTranslation();

    const loading = ref(false);

    async function getVideoInfos(url: string): Promise<VideoInfos | undefined> {
        try {
            loading.value = true;
            if (url.includes('youtu') && !url.includes('playlist')) {
                return await getYoutubeInfo(url);
            }

            if (url.includes('vimeo')) {
                return await getVimeoInfo(url);
            }
        } catch (e) {
            if (!(e instanceof VideoNotFoundError)) {
                logger.error('Cannot get video infos', e);
                pushVJoySnackbar({props: {message: t('generic.error.unexpected'), level: 'error'}});
                return undefined;
            }
        } finally {
            loading.value = false;
        }
        pushVJoySnackbar({props: {message: t('profile.portfolio.item.video.url.incorrect'), level: 'error'}});
        return undefined;
    }

    // YOUTUBE

    type YoutubeAPIResponse = {
        items: {
            snippet: {
                title: string;
                thumbnails: {
                    default: {url: string};
                    medium: {url: string};
                    high: {url: string};
                    standard: {url: string};
                    maxres: {url: string};
                };
            };
        }[];
    };

    async function getYoutubeInfo(url: string): Promise<VideoInfos> {
        const id = extractYoutubeIdFromUrl(url);
        if (!id) {
            throw new VideoNotFoundError(url);
        }

        try {
            const youtubeInfo = await $fetch<YoutubeAPIResponse>(
                `https://www.googleapis.com/youtube/v3/videos?key=AIzaSyB3y4FasBs9gT_iM9MsfC5LX8U121uIsn8&part=snippet&id=${id}`,
            );

            const snippet = youtubeInfo.items[0].snippet;

            const lowRes = snippet.thumbnails.medium?.url ?? snippet.thumbnails.default.url;
            const mediumRes = snippet.thumbnails.high?.url ?? lowRes;
            const highRes = snippet.thumbnails.maxres?.url ?? snippet.thumbnails.standard?.url ?? mediumRes;

            return {
                title: snippet.title,
                thumbnails: {
                    highResolutionUrl: highRes,
                    mediumResolutionUrl: mediumRes,
                    lowResolutionUrl: lowRes,
                },
            };
        } catch (error) {
            if (error instanceof FetchError && error.statusCode === 404) {
                throw new VideoNotFoundError(url);
            }
            throw error;
        }
    }

    // VIMEO

    type VimeoAPIResponse = {
        title: string;
        thumbnail_url: string;
    };

    async function getVimeoInfo(url: string): Promise<VideoInfos> {
        const {id} = extractVimeoIdFromUrl(url);
        if (!id) {
            throw new VideoNotFoundError(url);
        }
        try {
            // See https://developer.vimeo.com/api/oembed/videos
            const videoInfo: VimeoAPIResponse = await $fetch<VimeoAPIResponse>(
                `https://vimeo.com/api/oembed.json?url=${url.trim()}&width=1920&height=1920`,
            );

            const thumbnailUrl = videoInfo.thumbnail_url.split('?')[0];
            return {
                title: videoInfo.title,
                thumbnails: {
                    highResolutionUrl: thumbnailUrl,
                    mediumResolutionUrl: thumbnailUrl,
                    lowResolutionUrl: thumbnailUrl,
                },
            };
        } catch (error) {
            if (error instanceof FetchError && error.statusCode === 404) {
                throw new VideoNotFoundError(url);
            }
            throw error;
        }
    }

    return {
        loading: readonlyState(loading),
        getVideoInfos,
    };
}

export function extractYoutubeIdFromUrl(url: string): string | undefined {
    const regExp = /^.*((youtu\.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[7].length === 11 ? match[7] : undefined;
}

export function extractVimeoIdFromUrl(url: string): {id?: string; hash?: string} {
    const regExp = /(https:\/\/)?vimeo.com\/(\d+)(\/(\w+))?/;
    const match = url.match(regExp);

    if (!match || match.length <= 1) {
        return {};
    }

    return {id: match[2], hash: match[4]};
}
